import React, { useEffect, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  KeyboardEvents,
  KeyboardExtender,
  useKeyboardState,
} from 'react-native-keyboard-controller';
import { View } from 'react-native';

const MIN_SOFT_KEYBOARD_HEIGHT = 100;

type KeyboardBarSurface = 'primary' | 'tertiary';

interface KeyboardBarProps {
  children: React.ReactNode;
  isVisible: boolean;
  surface?: KeyboardBarSurface;
}

const isSoftKeyboardHeight = (height: number) =>
  height >= MIN_SOFT_KEYBOARD_HEIGHT;

/**
 * Tracks the soft keyboard from its will-show event so a consumer can dock before the keyboard finishes animating.
 * A hardware keyboard posts show events for the accessory view alone, so frames shorter than a soft keyboard do not count.
 * @returns true from keyboardWillShow until keyboardWillHide.
 */
const useIsKeyboardShowing = () => {
  const isKeyboardVisible = useKeyboardState((state) => state.isVisible);
  const keyboardHeight = useKeyboardState((state) => state.height);
  const [isShowing, setIsShowing] = useState(
    isKeyboardVisible && isSoftKeyboardHeight(keyboardHeight),
  );

  useEffect(() => {
    if (!isKeyboardVisible) {
      setIsShowing(false);
    }
  }, [isKeyboardVisible]);

  useEffect(() => {
    const subscriptions = [
      KeyboardEvents.addListener('keyboardWillShow', (event) =>
        setIsShowing(isSoftKeyboardHeight(event.height)),
      ),
      KeyboardEvents.addListener('keyboardWillHide', () => setIsShowing(false)),
    ];
    return () => {
      subscriptions.forEach((subscription) => subscription.remove());
    };
  }, []);

  return isShowing;
};

/**
 * Docks its children to the top edge of the soft keyboard while it is visible.
 */
export const KeyboardBar: React.FC<KeyboardBarProps> = ({
  children,
  isVisible,
  surface = 'primary',
}) => {
  const [styles] = useStyles();
  const isKeyboardShowing = useIsKeyboardShowing();
  const [isMounted, setIsMounted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const isExtenderEnabled = isVisible && isMounted;
  const isDocked = isExtenderEnabled && isKeyboardShowing;
  const isShown = isDocked && isExpanded;

  // the native extender crashes when enabled before its children mount so this useEffect is a workaround
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    setIsExpanded(isDocked);
  }, [isDocked]);

  return (
    <KeyboardExtender enabled={isExtenderEnabled}>
      <View
        accessibilityElementsHidden={!isShown}
        aria-hidden={!isShown}
        importantForAccessibility={isShown ? 'auto' : 'no-hide-descendants'}
        pointerEvents={isShown ? 'auto' : 'none'}
        style={[
          styles.row,
          surface === 'tertiary' ? styles.rowTertiary : styles.rowPrimary,
          !isShown && styles.rowCollapsed,
        ]}
      >
        {children}
      </View>
    </KeyboardExtender>
  );
};

const useStyles = createStyles((theme) => ({
  row: {
    alignItems: 'center',
    borderBottomColor: theme.sk.color.border.primary,
    borderBottomWidth: 1,
    borderTopColor: theme.sk.color.border.primary,
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
    width: '100%',
  },
  rowCollapsed: {
    borderBottomWidth: 0,
    borderTopWidth: 0,
    height: 0,
    overflow: 'hidden',
    paddingVertical: 0,
  },
  rowPrimary: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
  rowTertiary: {
    backgroundColor: theme.sk.color.bg.tertiary.default,
  },
}));
